/**
 * Stale-chunk recovery: report + reload the page exactly once when a
 * dynamic-import chunk fails to load (see `isChunkLoadError`). A single guarded
 * reload swaps the stale `index.html` for the fresh one, restoring valid chunk
 * hashes — the manual refresh users do today, automated.
 *
 * Loop safety: the guard flag is SET before reloading and cleared only on a
 * *successful* chunk load (via `lazyWithRetry`), never on shell mount. So a
 * genuinely-broken deploy (chunk still missing after reload → import never
 * resolves → guard never cleared) reloads at most once, then shows the
 * fallback. Storage is best-effort: if `sessionStorage` throws (Safari private
 * mode, blocked storage) we fall back to an in-memory flag and degrade CLOSED
 * (at most one reload), never into a loop.
 */
const GUARD_KEY = "verity:chunk-reload-attempted";

// Fallback when sessionStorage is unavailable. Module-level = per page load,
// which is exactly the scope we want (a reload starts a fresh module).
let inMemoryAttempted = false;

function readAttempted(): boolean {
  if (inMemoryAttempted) return true;
  try {
    return window.sessionStorage.getItem(GUARD_KEY) === "1";
  } catch {
    return inMemoryAttempted;
  }
}

function writeAttempted(): void {
  inMemoryAttempted = true;
  try {
    window.sessionStorage.setItem(GUARD_KEY, "1");
  } catch {
    /* storage blocked — the in-memory flag above still bounds us to one reload */
  }
}

/**
 * Clear the guard so a *later, distinct* stale-chunk event can reload again.
 * Called from `lazyWithRetry` when a chunk loads successfully = "we recovered".
 */
export function clearReloadAttempt(): void {
  inMemoryAttempted = false;
  try {
    window.sessionStorage.removeItem(GUARD_KEY);
  } catch {
    /* ignore */
  }
}

/** Testing seam: force-clear both stores. */
export function __resetChunkReloadGuardForTests(): void {
  clearReloadAttempt();
}

/**
 * Report (info level) and reload the page once to recover from a stale chunk.
 * Returns `true` if a reload was triggered, `false` if the guard already fired
 * (caller should render the fallback instead). Idempotent: concurrent callers
 * (e.g. the `vite:preloadError` listener AND the error boundary) collapse to one
 * reload because the guard is set synchronously before any async work.
 */
export function triggerChunkReload(): boolean {
  if (readAttempted()) return false;
  writeAttempted();

  let reloaded = false;
  const reload = () => {
    if (reloaded) return;
    reloaded = true;
    try {
      window.location.reload();
    } catch {
      /* nothing else we can do */
    }
  };

  // Hard cap: reload no matter what, even if the Sentry import/flush hangs.
  const cap = setTimeout(reload, 2500);

  // Sentry is lazy-imported to keep the SDK out of the eager bundle (mirrors
  // setSentryUser in lib/sentry-init.ts). `flush` lets the beacon survive the
  // reload so deploy-induced reloads are measurable. Reporting must never block
  // or prevent recovery.
  import("@sentry/react")
    .then((Sentry) => {
      Sentry.captureMessage("stale-chunk auto-reload", "info");
      return Sentry.flush(2000);
    })
    .catch(() => {
      /* observability mishap must not block recovery */
    })
    .finally(() => {
      clearTimeout(cap);
      reload();
    });

  return true;
}
